import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIonRouter } from "@ionic/react";
import { UserRound } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { observer } from "mobx-react-lite";
import TisitWordmark from "@assets/icons/tisit_logo_full.svg";
import { useUserService } from "@entities/User";
import { ROUTES } from "@shared/constants/routes";
import { cn } from "@/lib/utils";

interface AppNavbarProps {
  headerRef?: Ref<HTMLElement>;
  className?: string;
  style?: CSSProperties;
  logoStyle?: CSSProperties;
  logoContainerStyle?: CSSProperties;
  logoContainerClassName?: string;
  logoContainerRef?: Ref<HTMLDivElement>;
  avatarStyle?: CSSProperties;
  avatarRef?: Ref<HTMLButtonElement>;
  onLogoClick?: () => boolean | void;
  avatarVariant?: "default" | "plain";
  avatarActionSlot?: ReactNode;
  centerSlot?: ReactNode;
  centerSlotClassName?: string;
  centerSlotStyle?: CSSProperties;
  rightSlot?: ReactNode;
  logoAriaLabel?: string;
  logoTap?: {
    enabled?: boolean;
    messages?: readonly string[];
    onComplete?: () => void;
  };
}

const LOGO_EASTER_EGG = {
  minTaps: 5,
  maxTaps: 7,
  messages: [
    "да-да, привет",
    "готов заниматься?",
    "приятно ^^",
    "ну все, уже все",
  ],
} as const;

const getRandomLogoTapTarget = (): number =>
  Math.floor(
    Math.random() * (LOGO_EASTER_EGG.maxTaps - LOGO_EASTER_EGG.minTaps + 1),
  ) + LOGO_EASTER_EGG.minTaps;

export const AppNavbar = observer(
  ({
    headerRef,
    className = "",
    style,
    logoStyle,
    logoContainerStyle,
    logoContainerClassName = "",
    logoContainerRef,
    avatarStyle,
    avatarRef,
    onLogoClick,
    avatarVariant = "default",
    avatarActionSlot,
    centerSlot,
    centerSlotClassName,
    centerSlotStyle,
    rightSlot,
    logoAriaLabel = "TISIT",
    logoTap,
  }: AppNavbarProps) => {
    const nav = useIonRouter();
    const userService = useUserService();
    const avatarUrl = userService?.avatarUrl;
    const prefersReducedMotion = useReducedMotion();
    const [logoMessageIndex, setLogoMessageIndex] = useState<number | null>(
      null,
    );
    const logoTapCountRef = useRef(0);
    const logoNextMessageTapTargetRef = useRef(getRandomLogoTapTarget());
    const logoNextMessageIndexRef = useRef(0);
    const logoResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const logoMessageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const logoActionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const isLogoTapEnabled = logoTap?.enabled ?? true;
    const logoTapMessages = logoTap?.messages ?? LOGO_EASTER_EGG.messages;

    const openProfile = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      nav.push(ROUTES.overall.profile);
    };

    useEffect(() => {
      return () => {
        if (logoResetTimerRef.current) {
          clearTimeout(logoResetTimerRef.current);
        }
        if (logoMessageTimerRef.current) {
          clearTimeout(logoMessageTimerRef.current);
        }
        if (logoActionTimerRef.current) {
          clearTimeout(logoActionTimerRef.current);
        }
      };
    }, []);

    const showLogoEasterMessage = (messageIndex: number) => {
      if (logoMessageTimerRef.current) {
        clearTimeout(logoMessageTimerRef.current);
      }

      setLogoMessageIndex(messageIndex);
      logoMessageTimerRef.current = setTimeout(
        () => setLogoMessageIndex(null),
        messageIndex === logoTapMessages.length - 1 ? 2600 : 1900,
      );
    };

    const handleLogoClick = () => {
      if (onLogoClick?.()) {
        return;
      }

      if (!isLogoTapEnabled || logoTapMessages.length === 0) {
        return;
      }

      logoTapCountRef.current += 1;

      if (logoResetTimerRef.current) {
        clearTimeout(logoResetTimerRef.current);
      }
      logoResetTimerRef.current = setTimeout(() => {
        logoTapCountRef.current = 0;
        logoNextMessageTapTargetRef.current = getRandomLogoTapTarget();
      }, 2200);

      if (logoTapCountRef.current < logoNextMessageTapTargetRef.current) {
        return;
      }

      const messageIndex = logoNextMessageIndexRef.current;
      logoNextMessageIndexRef.current =
        (logoNextMessageIndexRef.current + 1) % logoTapMessages.length;
      logoTapCountRef.current = 0;
      logoNextMessageTapTargetRef.current = getRandomLogoTapTarget();

      showLogoEasterMessage(messageIndex);

      if (messageIndex !== logoTapMessages.length - 1) {
        if (logoActionTimerRef.current) {
          clearTimeout(logoActionTimerRef.current);
        }
        return;
      }

      if (logoActionTimerRef.current) {
        clearTimeout(logoActionTimerRef.current);
      }
      logoActionTimerRef.current = setTimeout(() => {
        logoTap?.onComplete?.();
      }, 650);
    };

    const defaultAvatarButton = (
      <button
        ref={avatarRef}
        type="button"
        aria-label="Открыть профиль"
        onClick={openProfile}
        style={avatarStyle}
        className={cn(
          "flex size-[48px] items-center justify-center overflow-hidden [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:ring-2 focus-visible:ring-black/[0.08] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FBFBFA]",
          avatarVariant === "plain"
            ? "rounded-full bg-transparent p-0 shadow-none ring-0"
            : "rounded-[18px] bg-[#F8F8F5]/80 p-[3px] shadow-[0_8px_22px_rgba(17,19,21,0.08)] ring-1 ring-black/[0.045]",
        )}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className={cn(
              "size-full object-cover",
              avatarVariant === "plain" ? "rounded-full" : "rounded-[15px]",
            )}
          />
        ) : (
          <UserRound className="size-6 text-[#777B80]" aria-hidden="true" />
        )}
      </button>
    );

    const defaultRightSlot = avatarActionSlot ? (
      <div className="relative z-20 flex items-center justify-end gap-2 overflow-visible">
        {avatarActionSlot}
        {defaultAvatarButton}
      </div>
    ) : (
      defaultAvatarButton
    );

    return (
      <header
        ref={headerRef}
        style={style}
        className={`sticky top-0 z-50 flex h-[calc(var(--app-safe-area-top)+96px)] shrink-0 items-end justify-between overflow-visible px-5 pb-[18px] pt-[var(--app-safe-area-top)] ${className}`}
      >
        <div
          ref={logoContainerRef}
          style={logoContainerStyle}
          className={`relative z-20 ${logoContainerClassName}`}
        >
          <motion.button
            type="button"
            aria-label={logoAriaLabel}
            onClick={handleLogoClick}
            style={logoStyle}
            whileTap={
              !prefersReducedMotion
                ? { scale: 0.92, y: 1, rotate: -1.2 }
                : undefined
            }
            transition={{
              type: "spring",
              stiffness: 620,
              damping: 28,
              mass: 0.55,
            }}
            className="-ml-2 -mt-2 flex h-[48px] w-[96px] origin-left items-center justify-start rounded-[12px] px-2 py-2 [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:ring-2 focus-visible:ring-black/[0.08] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FBFBFA]"
          >
            <img
              src={TisitWordmark}
              alt="TISIT"
              className="pointer-events-none h-[32px] w-[77px] object-contain object-left [filter:drop-shadow(0_1px_0_rgba(255,255,255,0.72))_drop-shadow(0_-1px_0_rgba(255,255,255,0.62))_drop-shadow(1px_0_0_rgba(255,255,255,0.58))_drop-shadow(-1px_0_0_rgba(255,255,255,0.58))_drop-shadow(0_3px_10px_rgba(255,255,255,0.62))]"
            />
          </motion.button>
          <AnimatePresence>
            {logoMessageIndex !== null && (
              <motion.div
                role="status"
                aria-live="polite"
                initial={{
                  opacity: 0,
                  scale: prefersReducedMotion ? 1 : 0.97,
                  y: prefersReducedMotion ? 0 : -3,
                }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  scale: prefersReducedMotion ? 1 : 0.98,
                  y: prefersReducedMotion ? 0 : -4,
                }}
                transition={{
                  duration: 0.18,
                  ease: [0.23, 1, 0.32, 1],
                }}
                className="absolute left-[92px] top-0 z-30 w-max max-w-[min(230px,calc(100vw-190px))] rounded-[12px] border border-[#EAEAEA] bg-[#F9F9F8] px-4 py-3 text-[14px] font-semibold leading-tight text-[#2F3437] shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
              >
                {logoTapMessages[logoMessageIndex]}
                <span className="absolute -left-[5px] top-1/2 size-3 -translate-y-1/2 rotate-45 border-b border-l border-[#EAEAEA] bg-[#F9F9F8]" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {centerSlot && (
          <div
            style={centerSlotStyle}
            className={cn(
              "absolute bottom-[18px] left-[108px] right-[76px] z-10 flex h-[48px] min-w-0 items-center justify-center text-center",
              centerSlotClassName,
            )}
          >
            {centerSlot}
          </div>
        )}
        {rightSlot ?? defaultRightSlot}
      </header>
    );
  },
);
